/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  createPlugin,
  createComponentExtension,
} from '@backstage/core-plugin-api';

/**
 * Knative Serving plugin for Backstage.
 *
 * @public
 */
export const knativeServingPlugin = createPlugin({
  id: 'knative-serving',
});

/**
 * Knative Service overview card for entity page.
 * Shows service status, URL, and basic info.
 *
 * @public
 */
export const KnativeServiceCard = knativeServingPlugin.provide(
  createComponentExtension({
    name: 'KnativeServiceCard',
    component: {
      lazy: () =>
        import('./components/KnativeServiceCard').then(m => m.KnativeServiceCard),
    },
  }),
);

/**
 * Knative Revisions table for entity page.
 * Shows all revisions with their status and traffic allocation.
 *
 * @public
 */
export const KnativeRevisionsCard = knativeServingPlugin.provide(
  createComponentExtension({
    name: 'KnativeRevisionsCard',
    component: {
      lazy: () =>
        import('./components/KnativeRevisionsCard').then(m => m.KnativeRevisionsCard),
    },
  }),
);

/**
 * Knative Traffic management card for entity page.
 * Allows viewing and editing traffic distribution.
 *
 * @public
 */
export const KnativeTrafficCard = knativeServingPlugin.provide(
  createComponentExtension({
    name: 'KnativeTrafficCard',
    component: {
      lazy: () =>
        import('./components/KnativeTrafficCard').then(m => m.KnativeTrafficCard),
    },
  }),
);

/**
 * Knative Autoscaling status card for entity page.
 * Shows current scaling metrics and configuration.
 *
 * @public
 */
export const KnativeAutoscalingCard = knativeServingPlugin.provide(
  createComponentExtension({
    name: 'KnativeAutoscalingCard',
    component: {
      lazy: () =>
        import('./components/KnativeAutoscalingCard').then(m => m.KnativeAutoscalingCard),
    },
  }),
);

/**
 * Full Knative Serving tab content for entity page.
 * Combines all Knative cards into a comprehensive view.
 *
 * @public
 */
export const EntityKnativeServingContent = knativeServingPlugin.provide(
  createComponentExtension({
    name: 'EntityKnativeServingContent',
    component: {
      lazy: () =>
        import('./components/EntityKnativeServingContent').then(
          m => m.EntityKnativeServingContent,
        ),
    },
  }),
);

